// Canvas host for a deterministic liquid-mercury avatar. The pixels come from
// the frozen generator (mercury.ts); this component only schedules the paint
// off the first frame and scales the backing store for the display's density.
//
// Cost control: the generator is O(size²) per pixel with several fbm() taps, so
// a paint is not free. Two guards keep it cheap — the work is deferred (idle /
// next frame, never blocking first paint) and results are memoized in a small
// module cache keyed by the exact inputs, so re-renders and remounts of the
// same avatar reuse pixels instead of recomputing them.

import { useEffect, useRef, type ReactNode } from 'react';
import { render, type AvatarMask } from './mercury.js';

export interface MercuryAvatarProps {
  /** The seed string — compose with candidateSeed(userId, avatarSeed). */
  seed: string;
  /** CSS size in px (square). The backing store is scaled up for density. */
  size: number;
  mask?: AvatarMask;
  className?: string;
}

// Render at a touch above device density so the metallic highlights stay crisp
// on high-DPI screens, but cap it: past 3× the extra pixels cost real time for
// no visible gain on an avatar this small.
const DENSITY = 1.25;
const MAX_SCALE = 3;

const CACHE_LIMIT = 50;
// Keyed by seed + backing-store pixel size + mask. ImageData is cheap to keep
// and lets a remount blit instead of recompute. Insertion-ordered LRU-ish: on
// overflow we evict the oldest key.
const cache = new Map<string, ImageData>();

function cacheKey(seed: string, pixelSize: number, mask: AvatarMask): string {
  return `${pixelSize}|${mask}|${seed}`;
}

function pixelsFor(seed: string, pixelSize: number, mask: AvatarMask): ImageData {
  const key = cacheKey(seed, pixelSize, mask);
  const hit = cache.get(key);
  if (hit) return hit;
  const rgba = render(seed, pixelSize, mask);
  const image = new ImageData(rgba, pixelSize, pixelSize);
  cache.set(key, image);
  if (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  return image;
}

/** Defer work to idle time, falling back to the next frame. Returns a canceler. */
function scheduleIdle(fn: () => void): () => void {
  const g = globalThis as {
    requestIdleCallback?: (cb: () => void) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  if (typeof g.requestIdleCallback === 'function') {
    const id = g.requestIdleCallback(fn);
    return () => g.cancelIdleCallback?.(id);
  }
  const raf = requestAnimationFrame(fn);
  return () => cancelAnimationFrame(raf);
}

export function MercuryAvatar({ seed, size, mask = 'circle', className }: MercuryAvatarProps): ReactNode {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = globalThis.devicePixelRatio || 1;
    const pixelSize = Math.max(1, Math.round(size * Math.min(dpr * DENSITY, MAX_SCALE)));
    canvas.width = pixelSize;
    canvas.height = pixelSize;

    let cancelled = false;
    const cancel = scheduleIdle(() => {
      if (cancelled) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.putImageData(pixelsFor(seed, pixelSize, mask), 0, 0);
    });

    return () => {
      cancelled = true;
      cancel();
    };
  }, [seed, size, mask]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={className}
      style={{ width: size, height: size }}
    />
  );
}
